import { useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { redirectToGoogleConsent } from './GoogleConsentNotice';
import { friendlyError } from '../lib/errorReporting';

// Shown once, next app open, to someone who clicked "Solicitar acceso" (Gmail
// request-access, see GOOGLE_OAUTH_VERIFIED in server.py) and has since been
// allowlisted — see App.js's needsGmailAccessGranted. Same push/email event
// covers people who don't open the app right away; whichever channel reaches
// them first stamps gmail_access_notified_at so the others skip (see that
// field's docstring on Profile). "Conectar ahora" jumps straight into the
// real OAuth redirect, same as the email CTA.
export default function GmailAccessGrantedModal({ onDone }) {
    const [busy, setBusy] = useState(false);

    const handleDismiss = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await api.ackGmailAccessGranted();
        } catch (_) { /* best effort — don't block them from closing this */ }
        finally {
            onDone();
        }
    };

    const handleConnect = async () => {
        if (busy) return;
        setBusy(true);
        try {
            api.ackGmailAccessGranted().catch(() => {});
            const went = await redirectToGoogleConsent(await api.connectGoogle());
            if (!went) setBusy(false);
        } catch (e) {
            setBusy(false);
            toast.error(friendlyError(e, 'No se pudo iniciar la conexión con Google'));
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85"
            data-testid="gmail-access-granted-modal"
            onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}
        >
            <div className="w-full max-w-md glass-card rounded-2xl p-6 sm:p-8 noise-overlay relative text-center animate-modal-in">
                <div className="w-16 h-16 rounded-full bg-violet-500/10 flex items-center justify-center mx-auto mb-5">
                    <Mail className="w-8 h-8 text-violet-400" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins' }}>
                    Tu acceso a Gmail está listo 📬
                </h2>
                <p className="text-slate-400 text-sm mb-6">
                    Tu solicitud fue aprobada. Conecta tu Gmail y MiPisto detecta tus compras
                    y pagos automáticamente, directo desde los correos de tu banco.
                </p>

                <button
                    type="button"
                    data-testid="gmail-access-granted-connect-btn"
                    onClick={handleConnect}
                    disabled={busy}
                    className="w-full flex items-center justify-center gap-2 bg-[#ffffff] hover:bg-[#f1f3f4] text-[#1f1f1f] h-11 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50"
                >
                    <Mail className="w-4 h-4 text-[#EA4335]" />
                    {busy ? 'Conectando...' : 'Conectar ahora'}
                </button>

                <button
                    type="button"
                    data-testid="gmail-access-granted-dismiss-btn"
                    onClick={handleDismiss}
                    disabled={busy}
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-300 mt-4 transition-colors disabled:opacity-50 active:opacity-60"
                >
                    Ahora no
                </button>
            </div>
        </div>
    );
}
